import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { apiClient } from '@/lib/api-client';

type Status = 'verifying' | 'ready' | 'invalid' | 'success';

export default function ChangePasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [status, setStatus] = useState<Status>('verifying');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate token on mount — without a valid token this page is a dead end
  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    apiClient.post('/auth/verify-reset-token', { token })
      .then(() => setStatus('ready'))
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleSubmit = async () => {
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain uppercase, lowercase, and a number');
      return;
    }
    if (password !== confirm) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', { token, password });
      setStatus('success');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Something went wrong. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="animate-spin h-6 w-6 border-2 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-red-200 dark:border-red-900 shadow-xl p-8 w-full max-w-md text-center">
          <XCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Link invalid or expired</h1>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            This password change link has expired or already been used. Return to settings to request a new one.
          </p>
          <button className="btn-primary px-6 py-2 text-sm" onClick={() => navigate('/settings')}>
            Back to Settings
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-green-200 dark:border-green-900 shadow-xl p-8 w-full max-w-md text-center">
          <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Password changed</h1>
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-5">
            Your password has been updated. You have been signed out of all devices — please log in again.
          </p>
          <button className="btn-primary px-6 py-2 text-sm" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 dark:from-brand-900/40 to-accent-50 dark:to-zinc-950 px-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-gray-200 dark:border-zinc-700 p-8 w-full max-w-md">
        <div className="text-center mb-7">
          <div className="inline-flex items-center justify-center h-13 w-13 rounded-2xl bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400 mb-4">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-zinc-100">Set new password</h1>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">Choose a strong password for your account.</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">New password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="••••••••"
                className="w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400"
              >
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Confirm password</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="btn-primary w-full py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Updating…
              </span>
            ) : 'Update Password'}
          </button>
        </div>
      </div>
    </div>
  );
}
